import React, { useMemo } from 'react'
import RoomBreakdownDetails from './RoomBreakdownDetails'
import RoomBreakdownLoadingSkeleton from './RoomBreakdownLoadingSkeleton'
import useOfferMetaData from '../../../../hooks/Offers/useOfferMetaData'
import { isBedbankSearchRoom, isLEOffer, isReservationSearchRoom } from 'lib/offer/offerTypes'
import useBedbankRoomBreakdown from '../../../../hooks/useBedbankRoomBreakdown'
import { getRoomBreakdown, getPackageRoomBreakdown, mapSearchOccupancyToOccupants } from 'lib/search/searchUtils'
import { pluralizeToString } from 'lib/string/pluralize'
import { excludeNullOrUndefined } from 'checkout/utils'
import { useMaldivesOffersPricingContext } from 'components/MaldivesDirect/MaldivesDirectSearchPage/hooks/useMaldivesOffersPricing'

interface Props {
  offer: App.HotelOffer | App.HotelOfferSummary | App.BedbankOffer | App.BedbankOfferSummary
  filters?: App.OfferListFilters
}

function SearchTileRoomBreakdown({
  offer,
  filters,
}: Props) {
  const metaData = useOfferMetaData(offer.id, filters)

  // Try to get package IDs from Maldives pricing context (for multi-room allocation)
  const maldivesPricing = useMaldivesOffersPricingContext()
  const maldivesPackageIds = maldivesPricing.pricingByOfferId[offer.id]?.packageIds

  const recommendedForText = useMemo(() => {
    const numberOfAdults = filters?.rooms?.reduce((sum, room) => sum + room.adults, 0) ?? 0
    const numberOfChildren = filters?.rooms?.reduce((sum, room) => sum + (room.teenagers ?? 0) + (room.children ?? 0) + (room.infants ?? 0), 0) ?? 0
    return `Recommended for ${numberOfAdults} adults${numberOfChildren > 0 ? `, ${pluralizeToString('child', numberOfChildren)}` : ''}`
  }, [filters?.rooms])

  const leRoomsList = useMemo(() => {
    const packages = isLEOffer(offer) ? offer.packages : []

    // If we have Maldives package IDs, use them directly via package ID
    if (maldivesPackageIds && maldivesPackageIds.length > 0) {
      return getPackageRoomBreakdown(maldivesPackageIds, packages)
    }

    // Otherwise, get room breakdown from metadata rooms
    const metaDataRooms = metaData?.rooms.filter(isReservationSearchRoom) ?? []
    return getRoomBreakdown(metaDataRooms, packages)
  }, [maldivesPackageIds, metaData?.rooms, offer])

  const bedbankRooms = useMemo(() =>
    metaData?.rooms
      .filter(isBedbankSearchRoom)
      .map(r => (r.rateId ?
          {
            rateId: r.rateId,
            propertyId: r.propertyId,
            occupancy: mapSearchOccupancyToOccupants(r.occupancy),
          }
        : undefined))
      .filter(excludeNullOrUndefined) ?? [],
  [metaData?.rooms])

  const { rooms: bedbankRoomsList, isLoading: isBedbankLoading } = useBedbankRoomBreakdown(bedbankRooms, filters)

  const allRooms = useMemo(() =>
    [...leRoomsList, ...bedbankRoomsList],
  [leRoomsList, bedbankRoomsList])

  if (isBedbankLoading) return <RoomBreakdownLoadingSkeleton />

  if (allRooms.length === 0) return null

  return (
    <RoomBreakdownDetails
      recommendedForText={recommendedForText}
      rooms={allRooms}
    />
  )
}

export default SearchTileRoomBreakdown
